import {
  addDays,
  coordsOf,
  DEFAULT_DURATION_MINUTES,
  eventUrl,
  hasEndTime,
  localDate,
  rsvpLabel,
  venueText,
  type Opportunity,
} from '../../shared/opportunity';
import { formatTimeRange } from '../../shared/taxonomy';

/**
 * Calendar export.
 *
 * A plain .ics download rather than an OAuth integration: Google Calendar,
 * Outlook, and Apple Calendar all import it, and nobody has to connect an
 * account to a beta.
 *
 * Times are emitted in UTC, straight from the record's own instants, so there
 * is no offset to assume and a daylight-saving boundary cannot move anything.
 * All-day events use DATE values on the day they fall on.
 */

/** RFC 5545 wants CRLF, and long lines folded at 75 octets. */
function fold(line: string): string {
  const octets = new TextEncoder().encode(line);
  if (octets.length <= 75) return line;

  const out: string[] = [];
  let current = '';
  let currentBytes = 0;

  for (const char of line) {
    const size = new TextEncoder().encode(char).length;
    // Continuation lines start with a space, so they carry one octet less.
    const limit = out.length === 0 ? 75 : 74;
    if (currentBytes + size > limit) {
      out.push(current);
      current = '';
      currentBytes = 0;
    }
    current += char;
    currentBytes += size;
  }
  if (current) out.push(current);

  return out.map((part, i) => (i === 0 ? part : ` ${part}`)).join('\r\n');
}

function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** An instant -> "20260922T130000Z" */
function toUtcStamp(at: string | number): string {
  const date = new Date(at);
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  );
}

/** All-day events use a DATE value, and DTEND is exclusive. */
function toDateValue(dayKey: string): string {
  return dayKey.replace(/-/g, '');
}

function describe(event: Opportunity, tentative: boolean): string {
  const parts: string[] = [];
  if (event.summary) parts.push(event.summary);

  const notes: string[] = [];
  if (event.isAllDay) {
    notes.push('This listing published no start time — check the event page for the schedule.');
  } else if (!hasEndTime(event)) {
    notes.push(
      `No end time was published. We assumed one hour (listed as ${formatTimeRange(event)}).`,
    );
  }
  if (tentative) notes.push('Saved for later — not yet confirmed on your My Climate Week schedule.');
  if (event.rsvpType !== 'open_to_public') notes.push(`RSVP: ${rsvpLabel(event.rsvpType)}.`);

  if (notes.length) parts.push(notes.join(' '));
  parts.push(`Added from My Climate Week — ${eventUrl(event)}`);

  return parts.join('\n\n');
}

function toVevent(event: Opportunity, tentative: boolean, stamp: string): string[] {
  const lines: string[] = ['BEGIN:VEVENT'];

  lines.push(`UID:${event.id}@myclimateweek.com`);
  lines.push(`DTSTAMP:${stamp}`);

  if (event.isAllDay) {
    const day = localDate(event);
    lines.push(`DTSTART;VALUE=DATE:${toDateValue(day)}`);
    lines.push(`DTEND;VALUE=DATE:${toDateValue(addDays(day, 1))}`);
  } else {
    const end = event.endsAt ?? Date.parse(event.startsAt) + DEFAULT_DURATION_MINUTES * 60_000;
    lines.push(`DTSTART:${toUtcStamp(event.startsAt)}`);
    lines.push(`DTEND:${toUtcStamp(end)}`);
  }

  lines.push(`SUMMARY:${escapeText(event.name)}`);
  lines.push(`DESCRIPTION:${escapeText(describe(event, tentative))}`);

  const location = [event.neighborhood, venueText(event)].filter(Boolean).join(' — ');
  if (location) lines.push(`LOCATION:${escapeText(location)}`);
  const coords = coordsOf(event);
  if (coords) lines.push(`GEO:${coords[0]};${coords[1]}`);

  lines.push(`URL:${eventUrl(event)}`);
  lines.push(`STATUS:${tentative ? 'TENTATIVE' : 'CONFIRMED'}`);
  // Saved-for-later shows as free time; a committed event shows as busy.
  lines.push(`TRANSP:${tentative ? 'TRANSPARENT' : 'OPAQUE'}`);
  if (event.topics.length) {
    lines.push(`CATEGORIES:${event.topics.map((t) => escapeText(t.label)).join(',')}`);
  }

  lines.push('END:VEVENT');
  return lines;
}

export interface IcsInput {
  scheduled: Opportunity[];
  /** Included as TENTATIVE when present. */
  saved?: Opportunity[];
}

export function buildIcs({ scheduled, saved = [] }: IcsInput): string {
  const stamp = toUtcStamp(Date.now());

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//CommonGround//My Climate Week//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:My Climate Week',
    'X-WR-TIMEZONE:America/New_York',
  ];

  for (const event of scheduled) lines.push(...toVevent(event, false, stamp));
  for (const event of saved) lines.push(...toVevent(event, true, stamp));

  lines.push('END:VCALENDAR');

  return `${lines.map(fold).join('\r\n')}\r\n`;
}

export function downloadIcs(content: string, filename = 'my-climate-week.ics'): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
